import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Search } from 'lucide-react'

function makeFlag(code: string): string {
  return (
    String.fromCodePoint(0x1f1e6 - 65 + code.charCodeAt(0)) +
    String.fromCodePoint(0x1f1e6 - 65 + code.charCodeAt(1))
  )
}

export const COUNTRIES = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BR', name: 'Brazil' },
  { code: 'CA', name: 'Canada' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EG', name: 'Egypt' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LY', name: 'Libya' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MX', name: 'Mexico' },
  { code: 'MA', name: 'Morocco' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'KR', name: 'South Korea' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'VN', name: 'Vietnam' },
].map((c) => ({ ...c, flag: makeFlag(c.code) }))

interface CountrySelectProps {
  label?: string
  value: string
  onChange: (code: string) => void
  error?: string
  placeholder?: string
}

export function CountrySelect({
  label,
  value,
  onChange,
  error,
  placeholder = 'Select country...',
}: CountrySelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const selected = COUNTRIES.find((c) => c.code === value)
  const filtered = search
    ? COUNTRIES.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    : COUNTRIES

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 50)
    }
  }, [open])

  const handleSelect = (code: string) => {
    onChange(code)
    setOpen(false)
    setSearch('')
  }

  const inputId = label?.toLowerCase().replace(/\s/g, '-')

  return (
    <div className="space-y-1.5" ref={containerRef}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-[var(--color-text-secondary)]"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <button
          id={inputId}
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`w-full h-10 px-3 flex items-center justify-between gap-2 rounded-[var(--radius-md)] border bg-[var(--color-bg-elevated)] text-sm transition-colors cursor-pointer
            ${error
              ? 'border-[var(--color-accent-red)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent-red)]'
              : 'border-[var(--color-border-default)] hover:border-[var(--color-border-strong)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent-amber)]'
            }
          `}
        >
          <span className={selected ? 'text-[var(--color-text-primary)] flex items-center gap-2' : 'text-[var(--color-text-muted)]'}>
            {selected ? (
              <>
                <span className="text-base leading-none">{selected.flag}</span>
                {selected.name}
              </>
            ) : (
              placeholder
            )}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-[var(--color-text-muted)] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>

        {open && (
          <div className="absolute z-50 mt-1 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] shadow-lg shadow-black/20">
            {/* Search input */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border-subtle)]">
              <Search className="h-3.5 w-3.5 text-[var(--color-text-muted)] shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search countries..."
                className="flex-1 bg-transparent text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] outline-none"
              />
            </div>

            {/* Country list */}
            <ul className="max-h-52 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-[var(--color-text-muted)]">No results</li>
              ) : (
                filtered.map((country) => (
                  <li key={country.code}>
                    <button
                      type="button"
                      onClick={() => handleSelect(country.code)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors cursor-pointer
                        ${country.code === value
                          ? 'bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]'
                          : 'text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]'
                        }
                      `}
                    >
                      <span className="text-base leading-none">{country.flag}</span>
                      {country.name}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-[var(--color-accent-red)]">{error}</p>}
    </div>
  )
}

export default CountrySelect
